import React from "react";
import { MapPin, Calendar, Clock, User, Phone, Mail, TrendingUp, Copy } from "lucide-react";
import { formatPrice, formatDateShort } from "@/lib/format";
import { CARD } from "@/constants/testIds";

export const ListingCard = ({ listing, onClaim, onDuplicate, index = 0 }) => {
  const isClaimed = listing.status === "claimed";
  return (
    <div
      data-testid={CARD.root(listing.id)}
      className="kw-card p-6 relative overflow-hidden kw-fade-up flex flex-col"
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
    >
      {isClaimed && (
        <div
          data-testid={CARD.claimedBadge(listing.id)}
          className="claimed-ribbon"
        >
          Claimed
        </div>
      )}

      {/* Address (leads) */}
      <div className="mb-3">
        <div className="font-display text-2xl font-bold uppercase tracking-tight text-[color:var(--kw-charcoal)] leading-[1.05]">
          {listing.address}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-[color:var(--kw-charcoal-soft)] mt-1.5">
          <MapPin size={14} strokeWidth={2.2} className="text-[color:var(--kw-red)]" />
          <span className="font-semibold">{listing.city}</span>
          <span className="text-[color:var(--muted-foreground)]/50">·</span>
          <span className="text-[color:var(--muted-foreground)]">{listing.county} County</span>
        </div>
      </div>

      {/* Price + DOM */}
      <div className="flex items-baseline gap-3 mb-4">
        <div className="font-display text-3xl font-black leading-none text-[color:var(--kw-charcoal)]">
          {formatPrice(listing.listPrice)}
        </div>
        <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--muted-foreground)]">
          <TrendingUp size={11} strokeWidth={2.5} />
          <span>{listing.daysOnMarket} days on market</span>
        </div>
      </div>

      {/* Date + Time */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-1.5 text-[color:var(--kw-charcoal)]">
          <Calendar size={14} strokeWidth={2.2} className="text-[color:var(--kw-teal-dark)]" />
          <span className="font-bold">{formatDateShort(listing.availableDate)}</span>
        </div>
        <span className="text-[color:var(--kw-border)]">|</span>
        <div className="flex items-center gap-1.5 text-[color:var(--kw-charcoal-soft)]">
          <Clock size={14} strokeWidth={2.2} />
          <span className="font-medium truncate">{listing.sellerPrefTime || "Flexible"}</span>
        </div>
      </div>

      {/* Notes */}
      {listing.notes && (
        <p className="text-sm text-[color:var(--kw-charcoal-soft)] line-clamp-2 leading-snug mb-4 italic">
          &ldquo;{listing.notes}&rdquo;
        </p>
      )}

      {/* Agent */}
      <div className="mb-5 mt-auto pt-4 border-t border-[color:var(--kw-border)]/60">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[color:var(--kw-red)] text-white flex items-center justify-center font-bold text-xs">
            {listing.agentName?.split(" ").map(n => n[0]).slice(0, 2).join("")}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm text-[color:var(--kw-charcoal)] truncate">{listing.agentName}</div>
            <div className="flex flex-wrap gap-x-3 text-[11px] text-[color:var(--muted-foreground)] leading-tight">
              <a href={`tel:${listing.agentPhone}`} className="inline-flex items-center gap-1 hover:text-[color:var(--kw-red)] transition-colors">
                <Phone size={10} /> {listing.agentPhone}
              </a>
              <a href={`mailto:${listing.agentEmail}`} className="inline-flex items-center gap-1 hover:text-[color:var(--kw-red)] transition-colors truncate">
                <Mail size={10} /> {listing.agentEmail}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Claimed by */}
      {isClaimed && listing.claimedBy && (
        <div className="mb-4 -mt-2">
          <div className="flex items-center gap-1.5 text-[color:var(--kw-teal-dark)] text-xs">
            <User size={12} strokeWidth={2.5} />
            <span className="font-bold uppercase tracking-widest">Claimed by</span>
          </div>
          <div className="text-sm font-bold text-[color:var(--kw-charcoal)] mt-0.5">
            {listing.claimedBy.firstName} {listing.claimedBy.lastName}
            <span className="font-normal text-[color:var(--muted-foreground)] ml-1.5">
              · {listing.claimedBy.office}
            </span>
          </div>
        </div>
      )}

      {/* Action */}
      <div className="mt-auto">
        {isClaimed ? (
          <button
            disabled
            className="w-full py-3 rounded-full text-sm font-black tracking-widest uppercase bg-[color:var(--kw-cream)] text-[color:var(--muted-foreground)] border border-[color:var(--kw-border)] cursor-not-allowed"
          >
            Opportunity Claimed
          </button>
        ) : (
          <button
            data-testid={CARD.claimBtn(listing.id)}
            onClick={() => onClaim(listing)}
            className="w-full py-3 rounded-full text-sm kw-btn-primary"
          >
            Claim Opportunity
          </button>
        )}
        {onDuplicate && (
          <button
            data-testid={CARD.duplicateBtn(listing.id)}
            onClick={() => onDuplicate(listing)}
            className="mt-2 w-full py-2.5 rounded-full text-[11px] font-black tracking-widest uppercase bg-white text-[color:var(--kw-charcoal-soft)] border border-[color:var(--kw-border)] hover:border-[color:var(--kw-charcoal)] hover:text-[color:var(--kw-charcoal)] transition-colors inline-flex items-center justify-center gap-1.5"
            title="Copy this opportunity for a new date"
          >
            <Copy size={12} strokeWidth={2.5} />
            Duplicate for a new date
          </button>
        )}
      </div>
    </div>
  );
};

export default ListingCard;
